import { useSessao } from '../../../auth/contexto'
import { useAnalisarChamado, useApoioDaIaNoChamado, useSituacaoDaIa } from '../../../api/queries/chamados'
import type { UsuarioLogado } from '../../../types/gmud'

/**
 * O apoio da IA no cartão da fiscalização — leitura do chamado e sugestão de julgamento.
 *
 * **Não é mais um cartão à parte, e a mudança é a razão de este arquivo existir.** Enquanto o
 * parecer morava num cartão abaixo do formulário, conferir uma resposta custava rolar a tela,
 * achar o trecho correspondente e voltar — e o custo caía justamente sobre quem a IA deveria
 * ajudar. Agora a leitura da IA e as perguntas do julgamento estão no mesmo cartão, com a
 * sugestão colada em cada pergunta.
 *
 * **E a leitura deixou de esperar por um clique.** Abrir a tela de um chamado fechado e ainda não
 * julgado já prepara o parecer no servidor; enquanto ele não fica pronto, o estado é `PREPARANDO` e
 * a consulta se refaz sozinha. O que a tela mostrava como "nenhuma análise feita" escondia cinco
 * situações diferentes — sendo preparada, no teto do mês, serviço fora, falha recente, ou de fato
 * ninguém pediu — e o servidor agora as distingue, com o motivo em pt-BR.
 *
 * O limite não mudou por causa disso, e o texto da tela repete: a IA sugere e explica; quem
 * responde, salva e assina é a fiscalização (Regra nº 3). Nenhum campo é gravado sem o clique do
 * fiscal, e o sistema registra quais sugestões ele manteve.
 */

/**
 * O estado do apoio da IA para um chamado.
 *
 * @param chamadoId qual chamado
 * @returns disponibilidade, o parecer guardado, o estado da leitura e o pedido de nova análise
 */
export function useApoioDaIa(chamadoId: number) {
  const { usuario } = useSessao()
  const situacao = useSituacaoDaIa()
  const apoio = useApoioDaIaNoChamado(chamadoId)
  const analisar = useAnalisarChamado(chamadoId)

  const parecer = apoio.data?.parecer ?? null

  return {
    /** Sem IA configurada, o bloco inteiro some: aviso de recurso ausente vira ruído. */
    disponivel: !situacao.isPending && !situacao.isError && (situacao.data?.disponivel ?? false),
    /** Em que pé está a leitura — sete estados, e nenhum deles é "erro". */
    estado: apoio.data?.estado ?? null,
    /** Por que o estado é este, em pt-BR, direto do servidor. */
    motivo: apoio.data?.motivo ?? null,
    parecer,
    /** O parecer só serve de sugestão quando concluiu — FALHOU não sugere nada. */
    parecerUtil: parecer?.situacao === 'CONCLUIDA' ? parecer : null,
    carregando: apoio.isPending,
    analisar,
    /**
     * O botão só aparece quando o papel permite E quando pedir faz sentido.
     *
     * `podePedir` vem do servidor: ele é falso enquanto a leitura está em voo (pedir de novo
     * colidiria com a linha em andamento) e quando o serviço de IA está fora — oferecer "refazer"
     * contra um serviço que está recusando é oferecer uma chamada paga que vai falhar.
     */
    podeAnalisar: podeAnalisar(usuario) && (apoio.data?.podePedir ?? true),
  }
}

/** Os mesmos papéis do `@PreAuthorize` do servidor — conveniência de tela, não regra. */
const PAPEIS_QUE_ANALISAM = ['FISCAL', 'ADMIN']

function podeAnalisar(usuario: UsuarioLogado | null): boolean {
  return usuario?.papeis.some((papel) => PAPEIS_QUE_ANALISAM.includes(papel)) ?? false
}
